import MUIButton from '@/components/MUI-Components/MUI-Button';
import React, {memo} from 'react';
import dynamic from 'next/dynamic';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import type {StaticImageData} from 'next/image';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
const SocialMedia = dynamic(() => import('@/components/Ui-components/SocialMedia'));
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import parse from 'html-react-parser';
import {colors, fontSize, fontWeight, lineHeight, spacing} from '@/styles/tokens';

interface DecodingProps {
    imageSrc: StaticImageData;
    alt?: string;
    heading: React.ReactNode;
    subHeading?: string[];
    buttonText?: string;
    openForm?: () => void;
    className?: string;
    isSocialMediaBtn?: boolean;
    imgClass?: string;
    imageBoxSx?: SxProps<Theme>;
    imgSx?: SxProps<Theme>;
}

const headingSx = {
    fontSize: {xs: fontSize[32], sm: fontSize[48]},
    fontWeight: fontWeight.extrabold,
    mb: spacing[24]
} as const;

const subHeadingSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight[28],
    color: colors.text.primary,
    mb: spacing[24]
} as const;

// Migrated from src/styles/_header.scss `.banner-container-grid` (inside `@include tabScreen()`,
// i.e. `max-width: 768px` per mixins.scss). Sole owner confirmed — this component's own Grid is the
// only consumer of the class anywhere in the repo.
const bannerContainerGridSx = {
    '@media (max-width: 768px)': {
        flexDirection: 'column-reverse !important'
    }
} as const;

// Migrated from globals.scss `.decoding-wrapper .decoding-img` (two responsive tiers: `@include
// tabScreen()` i.e. `max-width: 768px` at line 4203, and `@include mobileScreen()` i.e.
// `max-width: 430px` at line 4633 — per mixins.scss). Sole owner confirmed — this component's own
// DisplayImage (rendered as an actual <img> via `next/image`) is the only `.decoding-img` inside a
// `.decoding-wrapper` anywhere in the repo. Applied directly to DisplayImage's `sx` prop (it
// forwards `sx` straight onto the rendered <img>, no wrapper element needed).
const decodingImgResponsiveSx = {
    '@media (max-width: 768px)': {
        width: '500px !important' // no exact token match — preserved for parity
    },
    '@media (max-width: 430px)': {
        width: '100% !important'
    }
} as const;

const Decoding = (props: DecodingProps) => {
    const {imageSrc, alt = '', heading, subHeading, buttonText, openForm, className, isSocialMediaBtn, imgClass, imageBoxSx, imgSx} = props;
    return (
        <Box className={`decoding-wrapper ${className}`}>
            <Grid container spacing={4} className="banner-container-grid" alignItems="center" sx={bannerContainerGridSx}>
                <Grid item xs={12} sm={12} md={6} lg={6} textAlign="center">
                    <Box className="justify-center" sx={imageBoxSx}>
                        <DisplayImage
                            src={imageSrc}
                            alt={alt}
                            className={`banner-img decoding-img ${imgClass || ''}`}
                            sx={[decodingImgResponsiveSx, imgSx] as SxProps<Theme>}
                            priority={false}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                    <Box>
                        <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                            {heading}
                        </Typography>
                        {subHeading?.map((item: string, index: number) => (
                            <Box key={index + 1}>
                                <Typography component="div" className="font-regular-text remove-link-underline" sx={subHeadingSx}>
                                    {item ? parse(item) : ''}
                                </Typography>
                            </Box>
                        ))}
                        {buttonText && (
                            <MUIButton
                                endIcon={<ArrowForwardIcon />}
                                className="button-bg-primary animated-button moving-icon-button"
                                variant="contained"
                                label={buttonText}
                                onClick={openForm}
                            />
                        )}
                        {isSocialMediaBtn && <SocialMedia className="decoding-social-media" />}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default memo(Decoding);
